import { Workbox } from 'workbox-window';

/**
 * De service worker aanmelden, en het bijwerken beheersen (Stap 12).
 *
 * Eén registratie voor de hele app. Sinds Stap 9 registreerde `use-push.ts` `/sw.js` op het moment
 * dat iemand meldingen aanzette; dat kan niet meer, want de worker doet nu ook het offline werk en
 * moet er zijn vóórdat iemand ergens op tikt. Vandaar: bij het starten van de app, één keer.
 *
 * `registerType: 'prompt'` in de Vite-configuratie betekent dat een nieuwe versie **wacht**. Dat is
 * een keuze: een medewerker die midden in een instructie zit, hoort niet ineens een herladen scherm
 * te krijgen — en als hij net op "Gelezen en begrepen" drukte, is dat het slechtste moment dat er is.
 */

const OUTBOX_SYNC_TAG = 'up2d8-outbox';

let workbox: Workbox | null = null;
let waiting = false;
/** Heeft de medewerker zelf om de nieuwe versie gevraagd? Alleen dan mag de pagina herladen. */
let updateRequested = false;
const listeners = new Set<() => void>();

/** Meldt de worker aan. Idempotent: een tweede aanroep doet niets. */
export function registerServiceWorker(): void {
  if (workbox || !('serviceWorker' in navigator)) return;

  workbox = new Workbox('/sw.js');

  // `waiting` betekent: er staat een nieuwe versie klaar die wacht tot wij het zeggen.
  workbox.addEventListener('waiting', () => {
    waiting = true;
    for (const listener of listeners) listener();
  });

  /**
   * Herladen, maar alleen na een tik op "Vernieuwen".
   *
   * Bij het **eerste** bezoek neemt de worker ook de app over (`clients.claim`), en dat is geen nieuwe
   * versie. Zonder deze voorwaarde herlaadt de app dan midden in het aanmelden — en is het
   * personeelsnummer dat iemand net intypte weg. Precies dat gebeurde in de e2e-test van deze stap, en
   * het zou op een telefoon net zo goed gebeuren.
   */
  workbox.addEventListener('controlling', () => {
    if (updateRequested) window.location.reload();
  });

  void workbox.register();
}

/** Laat weten wanneer er een nieuwe versie klaarstaat. Geeft een opruimfunctie terug. */
export function onUpdateAvailable(listener: () => void): () => void {
  listeners.add(listener);
  if (waiting) listener();
  return () => listeners.delete(listener);
}

/** Zet de nieuwe versie aan; de pagina herlaadt daarna zelf. */
export function updateNow(): void {
  updateRequested = true;
  if (!workbox) {
    window.location.reload();
    return;
  }
  void workbox.messageSkipWaiting();
}

/**
 * Vraagt de browser om de wachtrij te versturen zodra er weer netwerk is.
 *
 * Werkt alleen waar Background Sync bestaat (Chrome, Edge, Android). Op iOS bestaat het niet, en daar
 * gebeurt het bij het openen van de app — dat staat ook in de instructie op `/app/install`, want het
 * is het verschil tussen "je bevestiging is onderweg" en "je bevestiging gaat mee zodra je de app
 * opent".
 */
export async function requestOutboxSync(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker?.ready;
    const sync = (
      registration as ServiceWorkerRegistration & {
        sync?: { register: (tag: string) => Promise<void> };
      }
    )?.sync;
    if (!sync) return false;

    await sync.register(OUTBOX_SYNC_TAG);
    return true;
  } catch {
    // Een browser die het niet kan of een gebruiker die achtergrondgegevens uitzette: geen fout,
    // alleen een kanaal minder.
    return false;
  }
}
